import { Link } from "react-router-dom";
import type { DirectoryCleaner } from "@/mocks/cleanersDirectory";
import { specialtyMeta } from "@/mocks/cleanersDirectory";

const badgeStyles: Record<string, { label: string; className: string; icon: string }> = {
  "top-rated": {
    label: "Top rated",
    className: "bg-primary-500 text-background-50",
    icon: "ri-trophy-line",
  },
  "rising-star": {
    label: "Rising star",
    className: "bg-secondary-500 text-background-50",
    icon: "ri-sparkling-line",
  },
  "eco-certified": {
    label: "Eco certified",
    className: "bg-accent-500 text-background-50",
    icon: "ri-leaf-line",
  },
};

const availabilityLabels: Record<DirectoryCleaner["availability"], { label: string; tone: string }> = {
  today: { label: "Available today", tone: "text-primary-700 bg-primary-100" },
  "this-week": { label: "This week", tone: "text-secondary-900 bg-secondary-100" },
  "next-week": { label: "Next week", tone: "text-foreground-700 bg-background-200" },
};

type Props = {
  cleaner: DirectoryCleaner;
  onView: (cleaner: DirectoryCleaner) => void;
};

export default function CleanerCard({ cleaner, onView }: Props) {
  const badge = cleaner.badge ? badgeStyles[cleaner.badge] : null;
  const availability = availabilityLabels[cleaner.availability];

  return (
    <article
      data-product-shop
      className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-colors flex flex-col"
    >
      <div className="relative h-72 md:h-64 overflow-hidden">
        <img
          src={cleaner.image}
          alt={`${cleaner.name}, ${cleaner.role}`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
        />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-background-50/95 backdrop-blur-sm text-foreground-900 text-xs font-medium px-3 py-1.5 rounded-full">
          <i className="ri-star-fill text-secondary-500"></i>
          {cleaner.rating}
        </span>
        {badge && (
          <span
            className={`absolute top-3 right-3 inline-flex items-center gap-1 ${badge.className} text-xs font-medium px-3 py-1.5 rounded-full`}
          >
            <i className={badge.icon}></i>
            {badge.label}
          </span>
        )}
        <span
          className={`absolute bottom-3 left-3 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full ${availability.tone}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
          {availability.label}
        </span>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-heading text-foreground-950 text-lg font-medium leading-tight truncate">
              {cleaner.name}
            </h3>
            <p className="text-foreground-600 text-sm mt-0.5">{cleaner.role}</p>
          </div>
          <p className="text-right whitespace-nowrap">
            <span className="font-heading text-foreground-950 text-lg font-medium">${cleaner.rate}</span>
            <span className="text-foreground-600 text-xs">/hr</span>
          </p>
        </div>

        <p className="text-foreground-700 text-sm mt-3 leading-relaxed line-clamp-2">{cleaner.bio}</p>

        <div className="flex flex-wrap gap-1.5 mt-4">
          {cleaner.specialties.slice(0, 3).map((s) => {
            const meta = specialtyMeta[s];
            if (!meta) return null;
            return (
              <span
                key={s}
                className="inline-flex items-center gap-1 bg-background-100 text-foreground-800 text-[11px] px-2.5 py-1 rounded-full"
              >
                <i className={`${meta.icon} text-[12px]`}></i>
                {meta.label}
              </span>
            );
          })}
        </div>

        <div className="grid grid-cols-3 gap-2 mt-5 pt-4 border-t border-background-200 text-xs">
          <div>
            <p className="text-foreground-600">Jobs</p>
            <p className="font-medium text-foreground-950 mt-0.5">{cleaner.jobs}</p>
          </div>
          <div>
            <p className="text-foreground-600">Years</p>
            <p className="font-medium text-foreground-950 mt-0.5">{cleaner.yearsExperience}</p>
          </div>
          <div>
            <p className="text-foreground-600">City</p>
            <p className="font-medium text-foreground-950 mt-0.5 truncate">{cleaner.city}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-5">
          <Link
            to="/booking"
            className="flex-1 text-center bg-foreground-950 hover:bg-primary-600 text-background-50 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
          >
            Book {cleaner.name.split(" ")[0]}
          </Link>
          <button
            type="button"
            onClick={() => onView(cleaner)}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-800"
            aria-label={`View ${cleaner.name}'s profile`}
          >
            <i className="ri-arrow-right-up-line"></i>
          </button>
        </div>
      </div>
    </article>
  );
}